import { useCallback, useState } from "react";
import { toast } from "sonner";
import { clockingApi } from "@/network/network.jsx";
import type { AttendanceDetail, TimeOffDetail } from "../types";

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback
  );
}

/** Cap parallel attendance mutations so bulk clear doesn't stampede the API. */
const BULK_CONCURRENCY = 5;
const BULK_TOAST_ID = "clocking-bulk-action";

async function runBulkAttendanceMutations(
  attendanceIds: number[],
  mutateOne: (id: number) => Promise<AttendanceDetail | null>,
  copy: {
    progress: (done: number, total: number) => string;
    success: (count: number) => string;
    failed: (count: number) => string;
  }
): Promise<AttendanceDetail[]> {
  const total = attendanceIds.length;
  if (!total) return [];

  const updated: AttendanceDetail[] = [];
  let failed = 0;
  let done = 0;
  let cursor = 0;

  toast.loading(copy.progress(0, total), { id: BULK_TOAST_ID });

  const worker = async () => {
    while (cursor < attendanceIds.length) {
      const id = attendanceIds[cursor++];
      try {
        const row = await mutateOne(id);
        if (row) updated.push(row);
        else failed += 1;
      } catch {
        failed += 1;
      } finally {
        done += 1;
        toast.loading(copy.progress(done, total), { id: BULK_TOAST_ID });
      }
    }
  };

  const poolSize = Math.min(BULK_CONCURRENCY, total);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));

  toast.dismiss(BULK_TOAST_ID);
  if (updated.length) toast.success(copy.success(updated.length));
  if (failed) toast.error(copy.failed(failed));
  return updated;
}

export function useClockingActions(boardId: number | string | null | undefined) {
  const [isActing, setIsActing] = useState(false);

  const withBoard = useCallback(() => {
    if (boardId == null) throw new Error("No board selected");
    return clockingApi(boardId);
  }, [boardId]);

  const getAttendanceDetail = useCallback(
    async (attendanceId: number): Promise<AttendanceDetail | null> => {
      try {
        const { data } = await withBoard().getAttendanceDetail(attendanceId);
        return data as AttendanceDetail;
      } catch (err) {
        toast.error(apiError(err, "Unable to load attendance detail."));
        return null;
      }
    },
    [withBoard]
  );

  const getTimeOffDetail = useCallback(
    async (timeOffId: number): Promise<TimeOffDetail | null> => {
      try {
        const { data } = await withBoard().getTimeOff(timeOffId);
        return data as TimeOffDetail;
      } catch (err) {
        toast.error(apiError(err, "Unable to load time off."));
        return null;
      }
    },
    [withBoard]
  );

  const markReviewedOne = useCallback(
    async (attendanceId: number, note?: string): Promise<AttendanceDetail | null> => {
      const { data } = await withBoard().markReviewed(attendanceId, note ? { note } : {});
      return data as AttendanceDetail;
    },
    [withBoard]
  );

  const excuseOne = useCallback(
    async (attendanceId: number, note?: string): Promise<AttendanceDetail | null> => {
      const { data } = await withBoard().excuse(attendanceId, note ? { note } : {});
      return data as AttendanceDetail;
    },
    [withBoard]
  );

  const markReviewed = useCallback(
    async (attendanceId: number, note?: string) => {
      setIsActing(true);
      try {
        const updated = await markReviewedOne(attendanceId, note);
        toast.success("Marked as reviewed");
        return updated;
      } catch (err) {
        toast.error(apiError(err, "Unable to mark reviewed."));
        return null;
      } finally {
        setIsActing(false);
      }
    },
    [markReviewedOne]
  );

  const excuse = useCallback(
    async (attendanceId: number, note?: string) => {
      setIsActing(true);
      try {
        const updated = await excuseOne(attendanceId, note);
        toast.success("Shift excused");
        return updated;
      } catch (err) {
        toast.error(apiError(err, "Unable to excuse shift."));
        return null;
      } finally {
        setIsActing(false);
      }
    },
    [excuseOne]
  );

  const bulkMarkReviewed = useCallback(
    async (attendanceIds: number[]): Promise<AttendanceDetail[]> => {
      if (!attendanceIds.length) return [];
      setIsActing(true);
      try {
        return await runBulkAttendanceMutations(attendanceIds, markReviewedOne, {
          progress: (done, total) => `Marking reviewed ${done}/${total}…`,
          success: (count) =>
            `Marked ${count} record${count === 1 ? "" : "s"} reviewed`,
          failed: (count) => `${count} could not be marked reviewed.`,
        });
      } finally {
        setIsActing(false);
      }
    },
    [markReviewedOne]
  );

  const bulkExcuse = useCallback(
    async (attendanceIds: number[]): Promise<AttendanceDetail[]> => {
      if (!attendanceIds.length) return [];
      setIsActing(true);
      try {
        return await runBulkAttendanceMutations(attendanceIds, excuseOne, {
          progress: (done, total) => `Excusing ${done}/${total}…`,
          success: (count) => `Excused ${count} record${count === 1 ? "" : "s"}`,
          failed: (count) => `${count} could not be excused.`,
        });
      } finally {
        setIsActing(false);
      }
    },
    [excuseOne]
  );

  const addNote = useCallback(
    async (attendanceId: number, note: string) => {
      setIsActing(true);
      try {
        const { data } = await withBoard().addNote(attendanceId, { note });
        toast.success("Note added");
        return data as AttendanceDetail;
      } catch (err) {
        toast.error(apiError(err, "Unable to add note."));
        return null;
      } finally {
        setIsActing(false);
      }
    },
    [withBoard]
  );

  const correct = useCallback(
    async (
      attendanceId: number,
      body: {
        clock_in_at?: string;
        clock_out_at?: string;
        clock_in_latitude?: number;
        clock_in_longitude?: number;
        clock_out_latitude?: number;
        clock_out_longitude?: number;
        note?: string;
      }
    ) => {
      setIsActing(true);
      try {
        const { data } = await withBoard().correct(attendanceId, body);
        toast.success("Times corrected");
        return data as AttendanceDetail;
      } catch (err) {
        toast.error(apiError(err, "Unable to correct times."));
        return null;
      } finally {
        setIsActing(false);
      }
    },
    [withBoard]
  );

  const patchTimeOff = useCallback(
    async (
      timeOffId: number,
      body: { approval_status: 2 | 3; admin_notes?: string }
    ): Promise<TimeOffDetail | null> => {
      setIsActing(true);
      try {
        const { data } = await withBoard().patchTimeOff(timeOffId, body);
        toast.success(body.approval_status === 2 ? "Time off approved" : "Time off denied");
        return data as TimeOffDetail;
      } catch (err) {
        toast.error(apiError(err, "Unable to update time off."));
        return null;
      } finally {
        setIsActing(false);
      }
    },
    [withBoard]
  );

  return {
    isActing,
    getAttendanceDetail,
    getTimeOffDetail,
    markReviewed,
    excuse,
    bulkMarkReviewed,
    bulkExcuse,
    addNote,
    correct,
    patchTimeOff,
  };
}
